import type { AppExt, LocaleString } from 'modapp';
import { Model } from 'modapp-resource';
import l10n from 'utils/l10n';

type Dictionary = Record<string, string>;

const dictionaries: Record<string, Dictionary> = {
	se: {}
};

const languages = [
	{ locale: 'se', name: l10n.l('language.swedish', "Svenska") },
	{ locale: 'en', name: l10n.l('language.english', "English") }
];

const iso639_1: Record<string, string> = {
	se: 'sv',
	en: 'en',
};

/**
 * Language handles setting the language file
 */
class Language {
	private model: Model<{ locale: string | null }>;
	private setLocalePromise: Promise<void>;
	private setLocalePromiseResolve: () => void;

	constructor(public app: AppExt, private params: { override?: string } = {}) {
		this.setLocalePromise = new Promise(resolve => this.setLocalePromiseResolve = resolve);
		this._init();
	}

	get l10n() {
		return l10n;
	}

	getModel() {
		return this.model;
	}

	getLanguages(): Array<{locale: string, name: LocaleString}> {
		return languages;
	}

	getSetLocalePromise() {

		return this.setLocalePromise;
	}

	_init() {
		this.model = new Model({ data: { locale: null }, eventBus: this.app.eventBus });

		this.setLocale(this.params.override || 'se')
			.catch(err => console.error("Error initiliazing locale: ", err))
			.finally(this.setLocalePromiseResolve);
	}

	async setLocale(locale?: string) {
		if (!locale) {
			locale = "se";
		}

		if (locale === this.model.locale) {
			return;
		}

		this.model.set({ locale });

		// Try to set lang attribute in HTML
		try {
			if (locale in iso639_1) {
				document.documentElement.setAttribute('lang', iso639_1[locale]);
			} else {
				document.documentElement.removeAttribute('lang');
			}
		} catch (ex) {
			console.error("Error setting document language: ", ex);
		}

		const dictionary = await this.getDictionary(locale);
		if (dictionary && locale == this.model.locale) {
			l10n.setLocale(locale, dictionary);
		}
	}

	async getDictionary(locale: string): Promise<Dictionary | null> {
		const d = dictionaries[locale];
		if (d) {
			return d;
		}

		try {
			const response = await fetch(this._toUrl(locale));
			const dictionary = await response.json();
			dictionaries[locale] = dictionary;
			return dictionary;
		} catch (err) {
			console.error("Error fetching locale file: " + this._toUrl(locale) + ": ", err);
			return null;
		}
	}

	_toUrl(locale: string) {
		return '/locale/' + locale + '.json';
	}

	dispose() {}
}

export default Language;
